import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import ExpandablePanel from '../../../../../../../common/widgets/ExpandablePanel';
import PopoverDialog from '../../../../../../../common/widgets/PopoverDialog';

import { ICategoryMenuItem } from '../../../../../Pages/BenefitsPage/types';
import { mapToMenuItems } from '../../../../../Pages/BenefitsPage/utils';
import {
  BenefitsContext,
  IBenefitsContext,
} from '../../../../../contexts/BenefitsContext';
import { BenefitType } from '../../../../../enums';
import { resources } from '../../../../../resources/resources';

import BenefitsNav from '../../BenefitsNav';

interface IBenefitNavProps {
  noColumns?: number;
  isTablet: boolean;
  benefitsType: BenefitType;
  closeMenu: () => void;
}

const benefitRootsMap: Partial<Record<BenefitType, keyof IBenefitsContext>> = {
  [BenefitType.Club]: 'benefitClubCategoryRoot',
  [BenefitType.Flexible]: 'flexibleBenefitsCategoryRoot',
  [BenefitType.DiscountVouchers]: 'discountVouchersCategoryRoot',
};

const typeOfBenefitMap: Record<BenefitType, string> = {
  [BenefitType.Flexible]: 'flexible-benefits',
  [BenefitType.Cashback]: 'cashback',
  [BenefitType.Club]: 'club-benefits',
  [BenefitType.DiscountVouchers]: 'discount-vouchers',
};

const benefitsResourceMap: Record<BenefitType, string> = {
  [BenefitType.Flexible]: 'flexibleBenefits',
  [BenefitType.Cashback]: 'cashback',
  [BenefitType.Club]: 'benefitClub',
  [BenefitType.DiscountVouchers]: 'discountVouchers',
};

const BenefitNav = ({
  noColumns,
  isTablet,
  benefitsType,
  closeMenu,
}: IBenefitNavProps) => {
  const [benefits, setBenefits] = useState<ICategoryMenuItem[] | undefined>();

  const { t: translate } = useTranslation();
  const { benefitRoots } = useContext(BenefitsContext);

  const typeOfBenefits = typeOfBenefitMap[benefitsType];
  const benefitsResource = benefitsResourceMap[benefitsType];

  const rootKey = benefitRootsMap[benefitsType];
  const rootCategory = rootKey ? benefitRoots[rootKey] : undefined;

  useEffect(() => {
    if (!rootCategory) {
      setBenefits(undefined);
      return;
    }

    setBenefits(mapToMenuItems(translate, rootCategory.children ?? []));
  }, [rootCategory, translate]);

  const label = translate(
    resources.menu[benefitsResource as keyof typeof resources.menu],
  );

  const hasNoBenefits = benefits?.length === 0;

  const content = useMemo(() => {
    if (benefitsType === BenefitType.Cashback) {
      return (
        <Link to={`/${typeOfBenefits}`} onClick={closeMenu}>
          <span>{label}</span>
        </Link>
      );
    }

    if (hasNoBenefits) {
      return (
        <Link to={`/${typeOfBenefits}`} onClick={closeMenu}>
          <span>{label}</span>
        </Link>
      );
    }

    if (isTablet) {
      return (
        <ExpandablePanel
          arrowType="downMobile"
          title={
            <Link
              to={`/${typeOfBenefits}`}
              onClick={closeMenu}
              className="nav-bar-menu-item"
            >
              <span>{label}</span>
            </Link>
          }
        >
          <BenefitsNav
            noColumns={noColumns ?? 3}
            benefitsType={typeOfBenefits}
            categories={benefits}
            onClick={closeMenu}
            showTaxes={benefitsType === BenefitType.Flexible}
          />
        </ExpandablePanel>
      );
    }

    return (
      <PopoverDialog
        className="category-nav-dropdown"
        placement="bottom"
        arrow=""
        closeFromContent
        type="hover"
        content={
          <BenefitsNav
            noColumns={noColumns ?? 3}
            benefitsType={typeOfBenefits}
            categories={benefits}
            showTaxes={benefitsType === BenefitType.Flexible}
          />
        }
      >
        <Link to={`/${typeOfBenefits}`}>
          <span>{label}</span>
        </Link>
      </PopoverDialog>
    );
  }, [
    hasNoBenefits,
    isTablet,
    typeOfBenefits,
    label,
    closeMenu,
    noColumns,
    benefits,
    benefitsType,
  ]);

  return (
    <li
      className={
        benefits && benefits.length > 0 ? 'menu-item-has-children' : ''
      }
    >
      {content}
    </li>
  );
};

export default BenefitNav;
